/**
 * Hex geometry — spec §3.
 *
 * Two distance functions, and the difference between them matters.
 *
 * `hexStepDistance` is the spec's axial formula: how many hexes you cross. It
 * defines ring membership and nothing else.
 *
 * `hexDistanceMetres` is the true centre-to-centre distance on the ground, and
 * it is what drone travel time is derived from. Spec §3 offers
 * `metres = hexDistance * 8660`, but that formula cannot reproduce the figures
 * the same section states: it gives 17,320 m for every ring-2 hex, while §3 says
 * ring 2 runs "15,000 m (edge hexes) to 17,320 m (axial hexes)". The prose is
 * the correct geometry and the multiplication is the simplification — two steps
 * at 60 degrees to each other land 8660 * sqrt(3) = 15,000 m away, not 17,320.
 *
 * Implemented as real geometry, which matches the stated figures exactly and
 * gives ring 2 internal texture: six near ring-2 hexes and six far ones, so
 * outer-ring routing is a decision rather than a flat surcharge. See
 * SPEC-ERRATA.md E7.
 */

import { HEX_CENTER_SPACING_M, WORLD_RINGS } from './constants';
import type { HexCoord, HexKey } from './types';

/** Canonical `"q,r"` key. The only place hex keys are formed. */
export function hexKey(coord: HexCoord): HexKey {
  return `${coord.q},${coord.r}`;
}

/** Parse a canonical hex key back into a coordinate. */
export function parseHexKey(key: HexKey): HexCoord {
  const [q, r] = key.split(',');
  return { q: Number(q), r: Number(r) };
}

/**
 * Hex keys in deterministic order (spec §11 rule 3). Every loop over hexes that
 * can affect state must go through this, never Object.keys directly.
 */
export function sortedHexKeys(hexes: Record<HexKey, unknown>): HexKey[] {
  return Object.keys(hexes).sort();
}

/** The six axial directions, in fixed order. */
const DIRECTIONS: readonly HexCoord[] = [
  { q: 1, r: 0 },
  { q: 1, r: -1 },
  { q: 0, r: -1 },
  { q: -1, r: 0 },
  { q: -1, r: 1 },
  { q: 0, r: 1 },
] as const;

/** Number of hexes crossed between two coordinates. spec §3 */
export function hexStepDistance(a: HexCoord, b: HexCoord): number {
  return (
    (Math.abs(a.q - b.q) + Math.abs(a.q + a.r - b.q - b.r) + Math.abs(a.r - b.r)) / 2
  );
}

/**
 * Centre-to-centre distance in metres, rounded to an integer.
 *
 * Axial -> cartesian with a 60-degree basis, then Euclidean. Math.sqrt is the
 * one transcendental-adjacent function that is safe here: IEEE 754 requires it
 * to be correctly rounded, so it is bit-identical across engines.
 */
export function hexDistanceMetres(a: HexCoord, b: HexCoord): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  // x = spacing * (dq + dr/2), y = spacing * dr * sqrt(3)/2
  const x = HEX_CENTER_SPACING_M * (dq + dr / 2);
  const y = HEX_CENTER_SPACING_M * dr * (Math.sqrt(3) / 2);
  return Math.round(Math.sqrt(x * x + y * y));
}

/** Ring index of a coordinate relative to the home hex. */
export function ringOf(coord: HexCoord): number {
  return hexStepDistance(coord, { q: 0, r: 0 });
}

/**
 * Every hex in the world, home first, then ring 1, then ring 2 — each ring in a
 * fixed rotational order. Deterministic by construction. spec §2: 19 hexes.
 */
export function worldHexes(rings: number = WORLD_RINGS): HexCoord[] {
  const out: HexCoord[] = [{ q: 0, r: 0 }];

  for (let ring = 1; ring <= rings; ring++) {
    // Start at `ring` steps along direction 4, then walk the six sides.
    let current: HexCoord = {
      q: DIRECTIONS[4]!.q * ring,
      r: DIRECTIONS[4]!.r * ring,
    };
    for (let side = 0; side < 6; side++) {
      for (let stepIndex = 0; stepIndex < ring; stepIndex++) {
        out.push({ q: current.q, r: current.r });
        const direction = DIRECTIONS[side]!;
        current = { q: current.q + direction.q, r: current.r + direction.r };
      }
    }
  }

  return out;
}
